/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { managedServiceApi } from '../../services/api';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { clampStorage, computeMonthlyTotal } from '../../pages/managed/pricing';
import { Button, Tag } from '../console/ui';
import { CloseIc } from '../console/icons';
import type {
  CatalogOffering,
  ManagedServiceInstance,
  ManagedServiceTier,
} from '../../types/managedServices';

export const formatMoney = (minorUnits: number, currency: string): string => {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol}${(minorUnits / 100).toFixed(2)}`;
};

export const formatSpec = (offering: CatalogOffering): string => {
  const vcpu = offering.cpu_millicores / 1000;
  const gib = offering.memory_mb / 1024;
  return `${vcpu % 1 === 0 ? vcpu : vcpu.toFixed(1)} vCPU · ${gib % 1 === 0 ? gib : gib.toFixed(1)} GiB`;
};

interface Props {
  productName: string;
  /** Offerings for one product, sorted by price ascending. */
  offerings: CatalogOffering[];
  initial?: { tier?: ManagedServiceTier; storage_gb?: number; name?: string };
  onClose: () => void;
}

/** Tier + storage + name picker for ordering one managed service. */
const OrderServiceModal: React.FC<Props> = ({ productName, offerings, initial, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProjectId, projects } = useSelectedProject();

  const initialOffering = offerings.find((o) => o.tier === initial?.tier) || offerings[0];
  const [selectedOfferingId, setSelectedOfferingId] = useState(initialOffering?.id || '');
  const [storageGB, setStorageGB] = useState<number>(
    initialOffering ? clampStorage(initialOffering, initial?.storage_gb ?? initialOffering.min_storage_gb) : 0,
  );
  const [name, setName] = useState(initial?.name || '');
  const [projectId, setProjectId] = useState(selectedProjectId);

  useEffect(() => {
    if (!projectId && selectedProjectId) setProjectId(selectedProjectId);
  }, [projectId, selectedProjectId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const selectedOffering = offerings.find((o) => o.id === selectedOfferingId) || null;
  const total = selectedOffering ? computeMonthlyTotal(selectedOffering, storageGB) : 0;

  const handleSelectTier = (offering: CatalogOffering) => {
    setSelectedOfferingId(offering.id);
    setStorageGB((prev) => clampStorage(offering, prev || offering.min_storage_gb));
  };

  const orderMutation = useMutation({
    mutationFn: () => {
      if (!selectedOffering || !projectId) {
        return Promise.reject(new Error('Missing offering or project'));
      }
      return managedServiceApi.order({
        product: selectedOffering.product,
        tier: selectedOffering.tier,
        storage_gb: storageGB,
        project_id: projectId,
        name: name.trim(),
      });
    },
    onSuccess: (res) => {
      const instance = res.data as ManagedServiceInstance;
      toast.success(`${instance.name} is being provisioned`);
      queryClient.invalidateQueries({ queryKey: ['managed-services'] });
      navigate('/managed-mlops/instances');
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to order managed service';
      if (status === 402) {
        toast(
          (t) => (
            <div className="space-y-2">
              <p className="text-sm font-medium text-white">Insufficient wallet balance</p>
              <p className="text-xs text-gray-300">{msg}</p>
              <div className="flex gap-2">
                <a
                  href="/portal/wallet?action=topup"
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs px-3 py-1 bg-primary-500 hover:bg-primary-600 text-white rounded"
                >
                  Top up wallet
                </a>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="text-xs px-3 py-1 border border-gray-600 text-gray-300 rounded"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          { duration: 15000, style: { background: '#1f2937', color: '#fff', maxWidth: 480 } },
        );
        return;
      }
      toast.error(msg);
    },
  });

  const canOrder =
    !!selectedOffering &&
    !!projectId &&
    name.trim().length > 0 &&
    storageGB >= selectedOffering.min_storage_gb &&
    storageGB <= selectedOffering.max_storage_gb &&
    !orderMutation.isPending;

  const product = offerings[0]?.product || 'service';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-title"
        className="ps-card w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto"
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-ps-border">
          <div className="flex flex-col gap-0.5">
            <span className="ps-eyebrow">Order</span>
            <h2 id="order-title" className="m-0 text-lg font-medium">
              {productName}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
          >
            <CloseIc className="w-5 h-5" />
          </button>
        </div>

        <form
          className="p-6 flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (canOrder) orderMutation.mutate();
          }}
        >
          <fieldset className="m-0 p-0 border-0">
            <legend className="ps-label">Tier</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {offerings.map((offering) => {
                const selected = offering.id === selectedOfferingId;
                return (
                  <button
                    key={offering.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => handleSelectTier(offering)}
                    className={`text-left rounded-[10px] border p-4 transition-colors ${
                      selected ? 'border-ps-accent bg-ps-accent-soft' : 'border-ps-border bg-ps-chrome hover:border-ps-strong'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ps-text capitalize">{offering.tier}</span>
                      {selected && <Tag tone="accent">Selected</Tag>}
                    </div>
                    <div className="text-xs text-ps-subtle mt-1">{formatSpec(offering)}</div>
                    <div className="text-sm font-medium text-ps-accent mt-2">
                      {formatMoney(offering.monthly_price, offering.currency)}/mo
                    </div>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {selectedOffering && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="managed-mlops-name" className="ps-label">
                  Instance name
                </label>
                <input
                  id="managed-mlops-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={`my-${product}-server`}
                  className="ps-input font-mono"
                />
              </div>
              <div>
                <label htmlFor="managed-mlops-project" className="ps-label">
                  Project
                </label>
                <select
                  id="managed-mlops-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="ps-input"
                >
                  {!projectId && <option value="">Select a project…</option>}
                  {projects.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="managed-mlops-storage" className="ps-label">
                  Storage (GB)
                </label>
                <input
                  id="managed-mlops-storage"
                  type="number"
                  min={selectedOffering.min_storage_gb}
                  max={selectedOffering.max_storage_gb}
                  value={storageGB}
                  onChange={(e) => setStorageGB(clampStorage(selectedOffering, Number(e.target.value)))}
                  className="ps-input sm:max-w-[200px]"
                />
                <p className="m-0 text-xs text-ps-subtle mt-1.5">
                  Between {selectedOffering.min_storage_gb} and {selectedOffering.max_storage_gb} GB ·{' '}
                  {formatMoney(selectedOffering.storage_price_per_gb_month, selectedOffering.currency)}/GB/mo
                </p>
              </div>
            </div>
          )}

          {projects.length === 0 && (
            <p className="m-0 text-xs text-ps-warn">You need a project before you can order a managed service.</p>
          )}

          {selectedOffering && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-ps-raised">
              <div>
                <p className="m-0 text-sm text-ps-muted">Estimated monthly total</p>
                <p className="m-0 text-2xl font-semibold text-ps-text">
                  {formatMoney(total, selectedOffering.currency)}
                  <span className="text-sm font-normal text-ps-muted">/mo</span>
                </p>
                <p className="m-0 text-xs text-ps-subtle">Billed monthly from the order date.</p>
              </div>
              <div className="flex gap-2.5">
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!canOrder}>
                  {orderMutation.isPending ? 'Ordering…' : 'Order'}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default OrderServiceModal;
